﻿import { FunctionComponent } from "react";
import { useEnvironmentContext, triggerDownloadPrompt } from "@am-tax/fe-core";
import { HStack, Icon, IconButton, Menu, MenuButton, MenuGroup, MenuItem, MenuList, Spacer, useDisclosure, useToast, Button } from "@chakra-ui/react";
import { IconCaretDown, IconFileDownload, IconFileImport, IconFileTypeCsv, IconFileTypeXls, IconFilterX } from "@tabler/icons-react";
import { AgGridReact } from "@ag-grid-community/react";
import { EntityCandidate } from "../../../../api/models/EntityCandidate";
import { useClientEntity } from "../../../../api";
import { useCandidateBulkExport } from "../../../../api/hooks/useCandidateBulkExport";
import { useCandidateDelete } from "../../../../api/hooks/useCandidateDelete";
import { ConfirmationDialog } from "../../../../components/ConfirmationDialog";

export interface CandidateGridMenuBarProps {
    clientId: string;
    entityId: string | undefined;
    grid: AgGridReact<EntityCandidate> | null;
    onImportModalOpen: () => void;
    clearFilterAndSortModels: () => void;
    refreshDataGrid: () => void;
    onExportCurrentGridViewToCsv: () => void;
    deleteIsDisabled: boolean;
}

const ImportIcon = <Icon as={IconFileImport} />,
    ExportIcon = <Icon as={IconFileTypeXls} />,
    ExportCsvIcon = <Icon as={IconFileTypeCsv} />,
    CaretDownIcon = <Icon as={IconCaretDown} />,
    FileDownloadIcon = <Icon as={IconFileDownload} />,
    FilterClearIcon = <Icon as={IconFilterX} />;

export const CandidateGridMenuBar: FunctionComponent<CandidateGridMenuBarProps> = ({
    grid,
    clientId,
    entityId,
    onImportModalOpen,
    clearFilterAndSortModels,
    refreshDataGrid,
    onExportCurrentGridViewToCsv,
    deleteIsDisabled,
}) => {
    const { apiUrl } = useEnvironmentContext();
    const entityQuery = useClientEntity(clientId, entityId ?? "", {
        enabled: entityId != undefined,
    });
    const toast = useToast();
    const candidatesExported = useCandidateBulkExport();
    const deleteCandidate = useCandidateDelete();

    const deleteDialog = useDisclosure();

    const deleteCandidates = async () => {
        deleteDialog.onOpen();
    };

    return (
        <HStack sx={{ backgroundColor: "blue.50" }} align="center">
            <IconButton
                variant={"ghost"}
                {...{
                    "aria-label": "clear all filters",
                    icon: FilterClearIcon,
                    onClick: async () => {
                        grid?.api.setFilterModel({});
                        clearFilterAndSortModels();
                    },
                }}
            />

            <Spacer />
            <Menu>
                <MenuButton as={Button} variant="ghost" isDisabled={deleteIsDisabled} onClick={deleteCandidates}>
                    Delete
                </MenuButton>
            </Menu>

            <Menu>
                <MenuButton as={Button} variant="ghost" rightIcon={CaretDownIcon}>
                    Import / Export
                </MenuButton>
                <MenuList>
                    <MenuGroup title="Import">
                        <MenuItem icon={FileDownloadIcon} as={"a"} href={`${apiUrl}api/CandidateTemplate`} isDisabled={entityQuery.data?.entityType !== "Leaf"}>
                            Download Template
                        </MenuItem>
                        <MenuItem icon={ImportIcon} onClick={onImportModalOpen} isDisabled={entityQuery.data?.entityType !== "Leaf"}>
                            Upload Candidates
                        </MenuItem>
                    </MenuGroup>
                    <MenuGroup title="Export">
                        <MenuItem
                            icon={ExportIcon}
                            as={"div"}
                            onClick={async () => {
                                try {
                                    const blob = await candidatesExported(clientId, entityId, false, true);
                                    triggerDownloadPrompt(blob, `Candidates.xlsx`);
                                } catch (ex) {
                                    toast({
                                        title: "Failure",
                                        description: "Candidate information could not be exported.",
                                        status: "error",
                                        duration: 5000,
                                        isClosable: true,
                                    });
                                    throw ex;
                                }
                            }}
                        >
                            Export All
                        </MenuItem>
                        <MenuItem icon={ExportCsvIcon} as={"div"} onClick={onExportCurrentGridViewToCsv}>
                            Export Current View
                        </MenuItem>
                    </MenuGroup>
                </MenuList>
            </Menu>
            <ConfirmationDialog
                isOpen={deleteDialog.isOpen}
                onClose={deleteDialog.onClose}
                title="Delete Candidates"
                confirmLabel="Yes"
                confirmColorScheme="red"
                isLoading={deleteCandidate.isLoading}
                onConfirm={async () => {
                    const selectedRows =
                        grid?.api
                            .getSelectedNodes()
                            .filter(m => m.data && m.data?.entityId && m.data?.id)
                            .map(m => ({
                                clientId: clientId,
                                entityId: m.data!.entityId,
                                candidateId: m.data!.id,
                            })) ?? [];

                    await deleteCandidate.mutateAsync(selectedRows);
                    refreshDataGrid();
                    deleteDialog.onClose();
                }}
            >
                Are you sure you want to delete?
            </ConfirmationDialog>
        </HStack>
    );
};
